import { dayjs } from '@utils/date'
import { ViewportTypeValues } from '@contexts/viewport'
import {
  TeaserVideo,
  TeaserFlexValue,
  TeaserParentFlex,
  ComputedTeaserFlex,
  TargetContentType,
} from './types'
import config from '@config'
import { layout } from '@measures/responsive'
import { DurationUnitType } from 'dayjs/plugin/duration'

const {
  date: { timeInputFormat: dateTimeInputFormat },
  video: { durationFormat: videoDurationFormat },
} = config

const computeFlex = ({
  parentFlex,
  hasParentGrid,
}: {
  parentFlex: TeaserParentFlex
  hasParentGrid: boolean
}): ComputedTeaserFlex => {
  const flexValue = parentFlex[0] as TeaserFlexValue

  return {
    desktop: flexValue,
    tablet:
      flexValue !== '6'
        ? flexValue === '4' && hasParentGrid
          ? '6'
          : '3'
        : '6',
    mobile: '6',
  }
}

//* Formula to calculate the width of a teaser, when we know its flex value
//* We also need to know:
//* -> CONTENT_AREA: the width of the area that hosts the teasers (currently 924)
//* -> MAX_FLEX: the flex value that represents a single teaser taking all the CONTENT_AREA space
//* -> BASE_FLEX: the flex value that represent a single teaser that takes the least amount of CONTENT_AREA space
//* -> GRID_GAP: the space between the teasers (if more than 1 exist in the same row - flex group)

//* First step: calculate the baseWidth, which is (CONTENT_AREA - (MAX_FLEX / BASE_FLEX - 1) * GRID_GAP) / (MAX_FLEX / BASE_FLEX) and expresses
//* the width of a teaser with a flex value of "BASE_FLEX"
//* Second step: calculate the teaserToBASE_FLEXRatio, which is: teaserFlex / BASE_FLEX, and expresses
//* how many times the teaser flex is bigger than the base flex
//* Third step: calculate the teaserToBASE_FLEXRatioInteger, which is Math.floor(teaserToBASE_FLEXRatio), and expresses
//* how many base flex teasers can fit fully inside of the teaser flex
//* Fourth step: calculate the teaserToBASE_FLEXRatioRemainder, which is teaserFlex % BASE_FLEX, and expresses
//* the amount of base flex teaser that is missing, in order for the teaser to fit another one
//* Fifth step: calculate the MAX_FLEXToTeaserFlexRatioInteger, which is Math.floor(MAX_FLEX / flexValue), and expresses
//* how manu time the teaser flex fits completely into the max flex

const computeTeaserWidth = ({
  viewportType,
  flexValue,
}: {
  viewportType: Exclude<ViewportTypeValues, ViewportTypeValues.MOBILE>
  flexValue: TeaserFlexValue
}): number => {
  const {
    maxFlex: MAX_FLEX,
    baseFlex: BASE_FLEX,
    gridGap: GRID_GAP,
    [viewportType]: { contentArea: CONTENT_AREA },
  } = layout

  const teaserFlex = parseInt(flexValue, 10)

  const baseWidth =
    (CONTENT_AREA - (MAX_FLEX / BASE_FLEX - 1) * GRID_GAP) /
    (MAX_FLEX / BASE_FLEX)
  const teaserToBASE_FLEXRatio = teaserFlex / BASE_FLEX
  const teaserToBASE_FLEXRatioInteger = Math.floor(teaserToBASE_FLEXRatio)
  const teaserToBASE_FLEXRatioRemainder = teaserFlex % BASE_FLEX
  const MAX_FLEXToTeaserFlexRatioInteger = Math.floor(MAX_FLEX / teaserFlex)

  return (
    teaserToBASE_FLEXRatio * baseWidth +
    (teaserToBASE_FLEXRatioInteger - 1) * GRID_GAP +
    (teaserToBASE_FLEXRatioRemainder / MAX_FLEXToTeaserFlexRatioInteger) *
      GRID_GAP
  )
}

const formatDuration = (duration: number) =>
  dayjs
    .duration(
      Number(duration),
      dateTimeInputFormat as unknown as DurationUnitType
    )
    .format(videoDurationFormat)

const getVideoData = (video?: TeaserVideo) => {
  if (!video) {
    return null
  }

  const { type, duration } = video

  if (duration === 0) {
    return null
  }

  if (duration < 0 && duration !== -1) {
    return null
  }

  return {
    type,
    duration,
    formattedDuration: formatDuration(duration),
  }
}

const isVideoOnlyArticle = (targetContentType?: TargetContentType) =>
  targetContentType === 'video'

export { computeFlex, computeTeaserWidth, getVideoData, isVideoOnlyArticle }
